import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  HiArrowRight, 
  HiBookOpen, 
  HiEyeSlash, 
  HiBellAlert, 
  HiBuildingLibrary,
  HiCodeBracket,
  HiCpuChip,
  HiSparkles,
  HiPaintBrush,
  HiArrowTrendingUp,
  HiServerStack
} from "react-icons/hi2";
import { trackOutboundLink } from "../Actions/analytics";

// Make sure to replace this with your actual Google Form URL
const GOOGLE_FORM_LINK = "https://forms.gle/8EdmtvhAZkAhWfzE7";

const HiringPage = () => {
  const revealRefs = useRef([]);

  // Simple Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    revealRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const addToRefs = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FAFAFA] selection:bg-sky-200 selection:text-sky-900 font-sans text-slate-900">
      
      {/* --- 1. HERO SECTION (DARK PREMIUM) --- */}
      <section className="relative pt-32 pb-24 lg:pt-48 lg:pb-32 overflow-hidden bg-[#0A0F1C]">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-20" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[400px] bg-sky-500/20 blur-[120px] rounded-full pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-8 text-xs font-bold text-sky-400 bg-sky-500/10 border border-sky-500/20 rounded-full uppercase tracking-widest">
            <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
            We're hiring interns
          </div>
          
          <h1 className="text-5xl md:text-7xl lg:text-[6rem] font-extrabold tracking-tighter leading-[1.05] mb-8 text-white">
            Build something <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 via-indigo-400 to-purple-400 italic pr-2">
              every student
            </span><br />
            <span className="text-slate-400">in India needs.</span>
          </h1>
          
          <p className="text-lg md:text-xl text-slate-400 font-medium leading-relaxed max-w-2xl mb-12">
            CampusIRA is solving the <strong className="text-white">biggest unsolved problem</strong> in engineering colleges — students preparing for placements completely blind, while the answers already exist in the heads of their seniors.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <a
              href={GOOGLE_FORM_LINK}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackOutboundLink(GOOGLE_FORM_LINK)}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white text-slate-950 px-8 py-4 rounded-full font-bold text-base hover:scale-105 hover:bg-sky-50 transition-all shadow-[0_0_30px_rgba(255,255,255,0.1)] group"
            >
              Apply via Google Form <HiArrowRight className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* --- 2. STATS STRIP --- */}
      <div className="border-y border-slate-200 bg-white py-12">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 divide-x divide-slate-100">
          {[
            { num: "1L+", label: "Target users · Phase 1" },
            { num: "500+", label: "Engineering colleges" },
            { num: "3", label: "Core problems solved" },
            { num: "Now", label: "We're building · Join us" }
          ].map((stat, idx) => (
            <div key={idx} ref={addToRefs} className="text-center opacity-0 translate-y-8 transition-all duration-700 delay-[100ms]">
              <div className="text-4xl font-black text-slate-900 tracking-tight mb-1">{stat.num}</div>
              <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* --- 3. THE PROBLEM --- */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div ref={addToRefs} className="opacity-0 translate-y-8 transition-all duration-700">
          <span className="text-sm font-bold tracking-widest text-sky-600 uppercase mb-3 block">The Problem</span>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mb-6">
            Every engineering student <br className="hidden md:block" /> faces the <span className="text-sky-600 italic">same friction.</span>
          </h2>
          <p className="text-lg text-slate-500 max-w-3xl leading-relaxed mb-16">
            We lived this. During exams, internships, and placements — the information students need exists, but it's buried in WhatsApp groups, random Telegram chats, and word-of-mouth. There's no organized, trustworthy, campus-aware home for it.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {[
            { icon: <HiBookOpen size={24} />, title: "The Resource Scramble", desc: "Before exams, students chase notes across disorganized WhatsApp groups. The same files get re-shared every semester. Hours wasted." },
            { icon: <HiEyeSlash size={24} />, title: "The Placement Blind Spot", desc: "When a company visits, juniors desperately DM seniors asking 'what did they ask?' Answers are lost. There's no system." },
            { icon: <HiBellAlert size={24} />, title: "The Opportunity Noise", desc: "Internships exist, but students get notification fatigue. Nothing is filtered for their specific college, branch, or profile." },
            { icon: <HiBuildingLibrary size={24} />, title: "The Campus Gap", desc: "Every college is different. The same company has different processes at different campuses. Global platforms miss this local nuance." }
          ].map((prob, idx) => (
            <div key={idx} ref={addToRefs} className="opacity-0 translate-y-8 transition-all duration-700 relative p-8 rounded-3xl bg-white border border-slate-200 hover:shadow-lg hover:-translate-y-1 transition-all">
              <div className="absolute top-6 right-8 text-6xl font-black text-slate-50 opacity-50 select-none pointer-events-none">0{idx + 1}</div>
              <div className="w-12 h-12 bg-sky-50 text-sky-600 rounded-xl flex items-center justify-center mb-6 border border-sky-100">
                {prob.icon}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 relative z-10">{prob.title}</h3>
              <p className="text-slate-600 relative z-10">{prob.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* --- 4. ROLES / OPEN POSITIONS --- */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div ref={addToRefs} className="opacity-0 translate-y-8 transition-all duration-700 mb-16">
            <span className="text-sm font-bold tracking-widest text-sky-400 uppercase mb-3 block">Open Roles</span>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6">
              We're looking for <span className="text-sky-400 italic">builders.</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl leading-relaxed">
              Write code that goes live to real users — not just practice projects. Ownership is real here.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { tag: "Frontend", title: "Frontend Developer", icon: <HiCodeBracket />, desc: "Build components tens of thousands of students will use. Work on the experience feed, onboarding, and design systems.", skills: ["React", "Next.js", "Tailwind"] },
              { tag: "Backend", title: "Backend Developer", icon: <HiServerStack />, desc: "Design APIs, schemas, and queues powering the platform. Own features from DB schema to production deployment.", skills: ["Node.js", "MongoDB", "Redis", "AWS"] },
              { tag: "Full Stack", title: "Full Stack Developer", icon: <HiCpuChip />, desc: "Work across the entire stack. Ideal if you want to understand how a modern production web app scales end to end.", skills: ["Next.js", "TypeScript", "System Design"] },
              { tag: "AI / ML", title: "AI Integration Eng", icon: <HiSparkles />, desc: "Build the magic: expected questions engine, data summarization, and integrate the Gemini API for mock interviews.", skills: ["Python", "LLM APIs", "Prompt Eng"] },
              { tag: "Design", title: "UI/UX Designer", icon: <HiPaintBrush />, desc: "Define our visual identity. Design flows that make CampusIRA the best product Indian students have ever used.", skills: ["Figma", "UI Systems", "User Research"] },
              { tag: "Growth", title: "Campus Growth", icon: <HiArrowTrendingUp />, desc: "Drive adoption. Build ambassador networks, manage social media pipelines, and execute the strategy to reach 1L+ users.", content: ["Marketing", "Community", "Analytics"] }
            ].map((role, idx) => (
              <div key={idx} ref={addToRefs} className="opacity-0 translate-y-8 transition-all duration-700 p-8 rounded-3xl bg-slate-800/50 border border-slate-700 hover:bg-slate-800 hover:border-sky-500/50 transition-colors">
                <span className="px-3 py-1 rounded-full bg-slate-900 text-sky-400 text-[10px] font-bold uppercase tracking-widest border border-slate-700 mb-6 inline-block">
                  {role.tag}
                </span>
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">{role.icon} {role.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">{role.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {(role.skills || role.content).map(skill => (
                    <span key={skill} className="px-2 py-1 rounded bg-slate-900/80 text-slate-300 text-xs font-mono border border-slate-700">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- 5. FOUNDER NOTE --- */}
      <section className="py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div ref={addToRefs} className="opacity-0 translate-y-8 transition-all duration-700 relative p-10 md:p-16 rounded-[2.5rem] bg-[#FAFAFA] border border-slate-200 shadow-xl overflow-hidden">
             {/* Quote Mark Decoration */}
             <div className="absolute -top-6 -left-2 text-[12rem] font-serif text-slate-100 leading-none select-none pointer-events-none">"</div>
             
             <p className="relative z-10 text-xl md:text-2xl font-medium text-slate-700 leading-relaxed italic mb-10">
               I built the first version of this platform because I was frustrated. During placements at IET DAVV, I spent hours hunting for notes and trying to find seniors who'd been interviewed. The info existed — it was just completely disorganized. 250 students found and used the first version with zero marketing. That told me everything. Now we're building the scalable architecture, and we want people who feel the same frustration to help build it.
             </p>
             
             <div className="flex items-center justify-center gap-4 relative z-10">
               <div className="w-12 h-12 rounded-full bg-slate-900 flex items-center justify-center text-white font-bold text-xl">C</div>
               <div className="text-left">
                 <div className="font-bold text-slate-900">Founder & Lead Architect</div>
                 <div className="text-sm font-medium text-slate-500">CampusIRA · Building for 15L+ Students</div>
               </div>
             </div>
          </div>
        </div>
      </section>

      {/* --- 6. FINAL CTA --- */}
      <section className="py-24 bg-[#0A0F1C] text-center px-6">
        <div ref={addToRefs} className="opacity-0 translate-y-8 transition-all duration-700 max-w-2xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-6">
            Ready to build <span className="text-sky-400 italic">something real?</span>
          </h2>
          <p className="text-slate-400 text-lg mb-10">
            We're reviewing applications on a rolling basis. If you want to build a platform you'll actually use on campus, fill in the form below. We'll reach out shortly.
          </p>
          <a
            href={GOOGLE_FORM_LINK}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackOutboundLink(GOOGLE_FORM_LINK)}
            className="inline-flex items-center justify-center gap-2 bg-sky-500 text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-sky-400 hover:scale-105 transition-all shadow-lg shadow-sky-500/20"
          >
            Apply via Google Form <HiArrowRight />
          </a>
        </div>
      </section>

    </div>
  );
};

export default HiringPage;